import { useEffect, useMemo, useRef } from "react"
import { createTutorialAnalytics } from "~/lib/tutorial-analytics"
import type { tutorials } from "~/lib/tutorials"

export function useTutorialAnalytics(
	id: keyof typeof tutorials,
	stepIndex: number
) {
	const tracker = useRef<
		ReturnType<typeof createTutorialAnalytics> | undefined
	>(undefined)
	const latestStep = useRef(stepIndex)
	latestStep.current = stepIndex
	useEffect(() => {
		const run = createTutorialAnalytics(id)
		tracker.current = run
		let canceled = false
		// Ignore Strict Mode's setup/cleanup probe; no tour was actually displayed.
		queueMicrotask(() => {
			if (canceled) return
			run.start()
			run.viewStep(latestStep.current)
		})
		return () => {
			canceled = true
			run.end("exited", "route_or_tab_left")
			if (tracker.current === run) tracker.current = undefined
		}
	}, [id])
	useEffect(() => {
		tracker.current?.viewStep(stepIndex)
	}, [stepIndex])
	return useMemo(
		() => ({
			finishStep: (skipped: boolean) => tracker.current?.finishStep(skipped),
			end: (
				...args: Parameters<ReturnType<typeof createTutorialAnalytics>["end"]>
			) => tracker.current?.end(...args)
		}),
		[]
	)
}
